import { Component, OnInit } from '@angular/core';
import { creFadeInOut } from 'cre-ng';
import dayjs from 'dayjs';

import { User } from '@app/core/core.model';
import {
  TeamChartInformation,
  TeamLeadMeasure,
  WarResultInformation,
} from '@app/core/driver-wig.model';
import { IDriverWigService } from '@app/core/services/interface/IDriverWigService';
import { ServiceFactory } from '@app/core/services/service.factory';
import { AuthService } from '@app/core/auth.service';
import { LogoService } from '@app/core/logo.service';
import { ChartData, ChartOptions } from 'chart.js';

@Component({
  animations: [creFadeInOut],
  selector: 'team-competition-chart',
  styleUrls: ['./team-competition-chart.component.css'],
  templateUrl: './team-competition-chart.component.html',
})
export class TeamCompetitionChartComponent implements OnInit {
  driverWigService: IDriverWigService;
  charInformation: TeamChartInformation;
  leadInformation: TeamLeadMeasure;
  battleName: string;
  isLoading: boolean;
  fleetLogoUrl: string;
  leagueLogoUrl: string;
  errorToShow: string;
  monthToShow: number;
  dateToShow: number;
  leadMonthToShow: number;
  leadDateToShow: number;
  hasData: boolean;
  chartData: ChartData;
  chartOptions: ChartOptions;
  goalToShow: string;
  leadGoalToShow: string;

  private currentUser: User;
  private monthShortNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  constructor(
    private logoService: LogoService,
    private authService: AuthService,
    private serviceFactory: ServiceFactory
  ) {
    this.driverWigService = serviceFactory.getDriverWigService();
  }

  ngOnInit() {
    this.hasData = false;
    this.errorToShow = null;
    this.currentUser = this.authService.getCurrentUser();
    this.isLoading = true;

    this.chartOptions = this.getChartOptions();

    void this.driverWigService
      .getDriverTeamBattleChart(this.currentUser.username)
      .then((data) => {
        this.charInformation = data;
        if (
          !this.charInformation.leadGoal ||
          this.charInformation.leadGoal === undefined ||
          this.charInformation.leadGoal === null
        ) {
          this.goalToShow = 'N/A';
          this.charInformation.battleStatus = 'N/A';
        } else {
          this.goalToShow = this.charInformation.leadGoal.toFixed(2) + '%';
        }
        const parseDate = new Date(this.charInformation.weekEnding);
        this.dateToShow = parseDate.getDate();
        this.monthToShow = parseDate.getMonth() + 1;
        this.battleName = 'Safe & On-Time % '; // Here we add the logic when have different battles

        if (this.charInformation.warResults.length > 0) {
          this.hasData = true;
          this.buildChartData(this.charInformation.warResults, this.charInformation.leadGoal);
        }

        void this.driverWigService
          .getDriverLeadMeasure(this.currentUser.username)
          .then((LeadData) => {
            this.leadInformation = LeadData;
            const leadParseDate = new Date(this.leadInformation.weekEnding);
            this.leadDateToShow = leadParseDate.getDate();
            this.leadMonthToShow = leadParseDate.getMonth() + 1;

            if (
              !this.leadInformation.leadGoal ||
              this.leadInformation.leadGoal === undefined ||
              this.leadInformation.leadGoal === null
            ) {
              this.leadGoalToShow = 'N/A';
              this.leadInformation.status = 'N/A';
            } else {
              this.leadGoalToShow = this.leadInformation.leadGoal.toFixed(2) + '%';
            }

            this.isLoading = false;
          })
          .catch((error) => {
            if (!!error.error && error.error) {
              this.errorToShow = error.error;
            }
            this.isLoading = false;
          });
      })
      .catch((error) => {
        if (!!error.error && error.error) {
          this.errorToShow = error.error;
        }
        this.isLoading = false;
      });
  }

  getStatusClass(status: string): string {
    if (this.goalToShow === 'N/A') {
      return 'loss';
    }
    if (status === 'Win') {
      return 'win';
    } else if (status === 'Loss') {
      return 'loss';
    } else {
      return 'win';
    }
  }

  private getChartOptions(): ChartOptions {
    return {
      legend: {
        display: false,
      },
      scales: {
        xAxes: [
          {
            gridLines: {
              display: false,
            },
          },
        ],
        yAxes: [
          {
            gridLines: {
              display: false,
            },
            stacked: false,
            ticks: {
              callback: (label, index, labels) => {
                return label + '%';
              },
              max: 100,
            },
          },
        ],
      },
      tooltips: {
        enabled: false,
      },
      plugins: {
        labels: {
          render: 'percentage',
          precision: 2,
        },
      },
    };
  }

  private buildChartData(data: WarResultInformation[], goal: number) {
    const fleetScoreData: number[] = [];
    const goalData: number[] = [];
    const labels: string[] = [];
    const barColors: string[] = [];

    data.forEach((fleetCompetitionDetail) => {
      fleetScoreData.push(fleetCompetitionDetail.war ? fleetCompetitionDetail.war : null);

      if (fleetCompetitionDetail.war >= goal) {
        barColors.push('#2C6F1A');
      } else {
        barColors.push('#FEFF44');
      }

      goalData.push(goal);
      const parseDateForChart = new Date(fleetCompetitionDetail.weekStart);
      const dateOfTheBar = parseDateForChart.getDate();
      const monthOfTheBar = this.monthShortNames[parseDateForChart.getMonth()];
      const labelToSet = monthOfTheBar + ' ' + dateOfTheBar.toString();
      labels.push(labelToSet);
    });

    if (data.length === 0) {
      this.chartOptions.scales.yAxes[0].ticks = {
        min: 0,
        max: 100,
      };
      this.hasData = false;
    } else {
      this.hasData = true;
    }

    this.chartData = {
      datasets: [
        {
          backgroundColor: '#727272',
          type: 'line',
          borderColor: '#727272',
          borderDash: [5, 5],
          borderWidth: 1,
          data: goalData,
          fill: false,
          lineTension: 0,
        },
        {
          type: 'bar',
          backgroundColor: barColors,
          borderWidth: 1,
          data: fleetScoreData,
          fill: true,
          lineTension: 1,
        },
      ],
      labels,
    };
  }
}
